/**
 * Anant Social Share Tool — 0-cost image/audio share suite.
 * Blueprint v3: subscription (500 AT/month or 5000 AT/year), 20 AT per AI caption,
 * client-side canvas + Web Share API, TTS/voice recording, product catalog, branding.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Share2, Sparkles, Mic, Save, Download, ImagePlus, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import AIWalletGuard from '../../components/AIWalletGuard';

const TABS = [
  { key: 'create', label: 'Create Post' },
  { key: 'catalog', label: 'Product Catalog' },
  { key: 'templates', label: 'Templates' },
  { key: 'my', label: 'My Posts' },
  { key: 'brand', label: 'Branding' },
  { key: 'plan', label: 'Plan' },
];

export default function AnantSocialShare() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('create');
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const load = () => api.get('/social-share/config').then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="p-6 text-center text-emerald-300">Loading...</div>;
  const active = !!cfg.active;

  const useTemplate = (t) => {
    setPendingTemplate({ ...t, _t: Date.now() });
    setTab('create');
    toast.success(`🎨 ${t.label} loaded — customize aur share karo`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-fuchsia-950/40 to-slate-950 text-white p-3 sm:p-6" data-testid="social-share-root">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-2xl sm:text-3xl font-black tracking-tight bg-gradient-to-r from-fuchsia-300 via-pink-300 to-amber-300 bg-clip-text text-transparent">📣 Anant Social Share</div>
            <div className="text-xs opacity-70">0-cost image + audio + share suite · Web Share API · 20 AT per AI caption</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="/wallets" data-testid="social-at-wallet-chip"
               className="px-3 py-1 rounded-full text-xs font-black bg-gradient-to-r from-amber-500/30 to-orange-500/30 border-2 border-amber-400/50 text-amber-100 hover:bg-amber-500/40 transition inline-flex items-center gap-1">
              💰 {Math.round(cfg.at_balance || 0)} AT
            </a>
            <PlanStatusBadge cfg={cfg} />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`social-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 whitespace-nowrap ${tab === t.key ? 'bg-fuchsia-500 border-fuchsia-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'create'    && <CreatePostTab cfg={cfg} active={active} onReload={load} pendingTemplate={pendingTemplate} onTemplateConsumed={() => setPendingTemplate(null)} />}
        {tab === 'catalog'   && <CatalogTab active={active} />}
        {tab === 'templates' && <TemplatesTab active={active} onUse={useTemplate} />}
        {tab === 'my'        && <MyPostsTab active={active} />}
        {tab === 'brand'     && <BrandingTab cfg={cfg} onReload={load} />}
        {tab === 'plan'      && <PlanTab cfg={cfg} onReload={load} />}
      </div>
    </div>
  );
}

// ============ CREATE POST TAB ============
function CreatePostTab({ cfg, active, onReload, pendingTemplate, onTemplateConsumed }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [caption, setCaption] = useState('');
  const [tone, setTone] = useState('friendly');
  const [lang, setLang] = useState('hi');
  const [intent, setIntent] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioLabel, setAudioLabel] = useState('');
  const [recording, setRecording] = useState(false);
  const [gradient, setGradient] = useState('linear-gradient(135deg,#7c3aed,#ec4899)');
  const [category, setCategory] = useState('');
  const [ttsVoice, setTtsVoice] = useState('');
  const recRef = useRef(null);
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState('');

  const branding = cfg.user || {};

  // Consume pending template from parent (from Templates tab click)
  useEffect(() => {
    if (!pendingTemplate) return;
    if (pendingTemplate.gradient) setGradient(pendingTemplate.gradient);
    if (pendingTemplate.sample_caption) setCaption(pendingTemplate.sample_caption);
    if (pendingTemplate.image_url) setImgSrc(pendingTemplate.image_url);
    setCategory(pendingTemplate.category || '');
    onTemplateConsumed && onTemplateConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTemplate?._t]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setImgSrc(rd.result);
    rd.readAsDataURL(f);
  };
  const addUrl = () => {
    if (!imgUrl) return;
    setImgSrc(imgUrl); setImgUrl('');
  };

  // Attractive canvas render — multi-stop gradient + logo + decorations + shadowed text
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    c.width = 1080; c.height = 1080;

    const paintBg = () => {
      // Base dark
      ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, c.width, c.height);
      // Rich gradient from template
      const stops = _parseGradientStops(gradient) || ['#7c3aed', '#ec4899', '#f59e0b'];
      const g = ctx.createLinearGradient(0, 0, c.width, c.height);
      stops.forEach((s, i) => g.addColorStop(i / Math.max(1, stops.length - 1), s));
      ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, c.height);
      // Radial highlight (soft glow top-left)
      const rg = ctx.createRadialGradient(c.width * 0.2, c.height * 0.15, 40, c.width * 0.2, c.height * 0.15, c.width * 0.55);
      rg.addColorStop(0, 'rgba(255,255,255,0.35)'); rg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = rg; ctx.fillRect(0, 0, c.width, c.height);
      // Diagonal texture stripes (very subtle)
      ctx.save(); ctx.globalAlpha = 0.05; ctx.strokeStyle = '#fff'; ctx.lineWidth = 3;
      for (let i = -1000; i < c.width * 2; i += 40) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + c.height, c.height); ctx.stroke();
      }
      ctx.restore();
    };

    const drawCategoryDecor = () => {
      if (!category) return;
      ctx.save();
      const cat = String(category).toLowerCase();
      if (cat.includes('festival')) {
        // Stars burst
        ctx.font = '80px sans-serif';
        const positions = [[80, 130], [c.width - 130, 180], [c.width - 100, c.height - 380], [140, c.height - 420]];
        const glyphs = ['✨', '🌟', '⭐', '💫'];
        positions.forEach((p, i) => { ctx.globalAlpha = 0.55; ctx.fillText(glyphs[i % glyphs.length], p[0], p[1]); });
      } else if (cat.includes('offer') || cat.includes('sale')) {
        // Starburst badge top-right
        ctx.translate(c.width - 180, 180); ctx.rotate(-0.2);
        ctx.fillStyle = '#facc15';
        for (let a = 0; a < 16; a++) {
          ctx.rotate(Math.PI / 8);
          ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -120); ctx.lineTo(20, -60); ctx.closePath(); ctx.fill();
        }
        ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(0, 0, 80, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.font = 'bold 40px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('SALE', 0, 0);
      } else if (cat.includes('national')) {
        // Flag stripes bottom
        ctx.fillStyle = '#f59e0b'; ctx.fillRect(0, c.height - 400, c.width, 12);
        ctx.fillStyle = '#059669'; ctx.fillRect(0, c.height - 386, c.width, 12);
      } else if (cat.includes('greeting')) {
        ctx.font = '90px sans-serif'; ctx.globalAlpha = 0.5;
        ctx.fillText('🌸', 60, 160); ctx.fillText('🌺', c.width - 160, 200);
      }
      ctx.restore();
    };

    const drawCaption = () => {
      // Caption block bottom with rounded top
      const capH = 320;
      const grad = ctx.createLinearGradient(0, c.height - capH, 0, c.height);
      grad.addColorStop(0, 'rgba(0,0,0,0.15)'); grad.addColorStop(0.35, 'rgba(0,0,0,0.75)'); grad.addColorStop(1, 'rgba(0,0,0,0.92)');
      ctx.fillStyle = grad; ctx.fillRect(0, c.height - capH, c.width, capH);
      // Accent bar
      ctx.fillStyle = '#facc15'; ctx.fillRect(50, c.height - capH + 25, 90, 8);
      // Caption text (shadowed)
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.9)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 3;
      ctx.fillStyle = '#fff'; ctx.font = 'bold 56px "Segoe UI", "Noto Sans Devanagari", sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      wrapText(ctx, caption || 'Aapka caption yahan aayega…', 50, c.height - capH + 55, c.width - 100, 66);
      ctx.restore();
      // Branding footer
      if (branding.brand_shop_name || branding.brand_phone) {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 8;
        ctx.fillStyle = '#fbbf24'; ctx.font = 'bold 36px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(branding.brand_shop_name || '', 50, c.height - 100);
        if (branding.brand_phone) {
          ctx.fillStyle = '#fff'; ctx.font = 'bold 28px sans-serif';
          ctx.fillText(`📞 ${branding.brand_phone}`, 50, c.height - 55);
        }
        ctx.restore();
      }
    };

    const drawBorderFrame = () => {
      ctx.save(); ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 6;
      ctx.strokeRect(20, 20, c.width - 40, c.height - 40);
      ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 2;
      ctx.strokeRect(36, 36, c.width - 72, c.height - 72);
      ctx.restore();
    };

    const drawLogoAndFinish = () => {
      if (branding.brand_logo_url) {
        const logo = new Image(); logo.crossOrigin = 'anonymous';
        logo.onload = () => {
          // Draw logo top-right in a circle
          const size = 160;
          const x = c.width - size - 60, y = 60;
          ctx.save();
          ctx.beginPath(); ctx.arc(x + size / 2, y + size / 2, size / 2 + 6, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.fill();
          ctx.beginPath(); ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(logo, x, y, size, size);
          ctx.restore();
          drawCaption(); drawBorderFrame();
        };
        logo.onerror = () => { drawCaption(); drawBorderFrame(); };
        logo.src = branding.brand_logo_url;
      } else {
        drawCaption(); drawBorderFrame();
      }
    };

    if (imgSrc) {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ratio = Math.max(c.width / img.width, c.height / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        ctx.drawImage(img, (c.width - w) / 2, (c.height - h) / 2, w, h);
        // Dark vignette overlay for text legibility
        const vg = ctx.createRadialGradient(c.width / 2, c.height / 2, c.width * 0.3, c.width / 2, c.height / 2, c.width * 0.7);
        vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, 'rgba(0,0,0,0.55)');
        ctx.fillStyle = vg; ctx.fillRect(0, 0, c.width, c.height);
        drawCategoryDecor(); drawLogoAndFinish();
      };
      img.onerror = () => { paintBg(); drawCategoryDecor(); drawLogoAndFinish(); };
      img.src = imgSrc;
    } else {
      paintBg(); drawCategoryDecor(); drawLogoAndFinish();
    }
  }, [imgSrc, caption, gradient, category, branding.brand_shop_name, branding.brand_phone, branding.brand_logo_url]);

  const aiCaption = async () => {
    if (!active) { toast.error('Active plan required'); return; }
    if (!intent || intent.length < 5) { toast.error('Intent likhein (5+ chars)'); return; }
    if (!window.confirm('AI Caption par 20 AT deduct hoga. Continue?')) return;
    setAiBusy(true);
    try {
      const r = await api.post('/social-share/ai-caption', { intent, tone, language: lang, include_hashtags: true });
      setCaption(r.data.caption || '');
      toast.success('✨ AI caption ready · 20 AT charged');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI caption failed');
    } finally { setAiBusy(false); }
  };

  const speakCaption = () => {
    if (!caption) { toast.error('Caption likhein'); return; }
    try {
      const u = new SpeechSynthesisUtterance(caption);
      u.lang = lang === 'en' ? 'en-IN' : lang === 'mr' ? 'mr-IN' : 'hi-IN';
      if (ttsVoice) {
        const v = window.speechSynthesis.getVoices().find((vv) => vv.name === ttsVoice);
        if (v) u.voice = v;
      }
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { toast.error('Browser TTS not available'); }
  };

  const toggleRecord = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Use opus codec — WhatsApp treats .opus as native voice-note (auto-preview + play)
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus' : 'audio/webm');
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        // Repack as .opus so WhatsApp shows it as a voice note (plays inline)
        const blob = new Blob(chunks, { type: 'audio/ogg;codecs=opus' });
        setAudioBlob(blob); setAudioLabel('🎙 Aap ki awaaz');
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
      };
      rec.start(); recRef.current = rec; setRecording(true);
    } catch { toast.error('Mic permission denied'); }
  };

  const genJingle = async (kind) => {
    // 0-cost audio jingles built with Web Audio API — auto-plays inline on WA as .opus voice note
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const offline = new OfflineAudioContext(1, 44100 * 3, 44100); // 3 sec mono
      const now = 0;
      const beep = (freq, at, dur, gainVal = 0.35, type = 'sine') => {
        const osc = offline.createOscillator(); osc.type = type; osc.frequency.value = freq;
        const g = offline.createGain();
        g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(gainVal, at + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
        osc.connect(g).connect(offline.destination);
        osc.start(at); osc.stop(at + dur + 0.05);
      };
      const noise = (at, dur, gainVal = 0.15) => {
        const buffer = offline.createBuffer(1, 44100 * dur, 44100);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
        const src = offline.createBufferSource(); src.buffer = buffer;
        const g = offline.createGain(); g.gain.value = gainVal;
        src.connect(g).connect(offline.destination); src.start(at);
      };
      if (kind === 'bell') {
        beep(1200, now, 0.4, 0.5); beep(900, now + 0.15, 0.6, 0.35); beep(1500, now + 0.3, 0.8, 0.4);
      } else if (kind === 'cash') {
        beep(1800, now, 0.08, 0.4, 'square'); beep(2200, now + 0.1, 0.08, 0.4, 'square');
        beep(2600, now + 0.2, 0.15, 0.4, 'square'); noise(now + 0.4, 0.6, 0.08);
      } else if (kind === 'whoosh') {
        noise(now, 1.2, 0.25); beep(200, now, 1.2, 0.3, 'sawtooth');
      } else if (kind === 'festive') {
        beep(523, now, 0.2, 0.4); beep(659, now + 0.15, 0.2, 0.4); beep(784, now + 0.3, 0.2, 0.4); beep(1046, now + 0.5, 0.6, 0.45);
      } else if (kind === 'dhol') {
        for (let t = 0; t < 2.5; t += 0.25) { beep(100 + Math.random() * 30, t, 0.08, 0.5, 'square'); }
      }
      const rendered = await offline.startRendering();
      // Encode as WAV blob (universally opens in WA, plays as audio)
      const blob = _audioBufferToWav(rendered);
      setAudioBlob(blob); setAudioLabel(`🔔 ${kind}`);
      toast.success(`Jingle ready · ${kind}`);
    } catch (e) { toast.error('Audio generation failed'); }
  };

  const downloadImage = () => {
    if (!active) { toast.error('Active plan required'); return; }
    const c = canvasRef.current; if (!c) return;
    const url = c.toDataURL('image/jpeg', 0.9);
    const a = document.createElement('a'); a.href = url; a.download = `anant-social-${Date.now()}.jpg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const nativeShare = async () => {
    if (!active) { toast.error('Active plan required'); return; }
    const c = canvasRef.current; if (!c) return;
    const imgBlob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.9));
    const imgFile = new File([imgBlob], 'anant-social.jpg', { type: 'image/jpeg' });
    // Audio file: WhatsApp treats .opus as voice-note, .mp3/.wav as file attachment.
    // We keep .opus for native share (WA auto-preview) and mirror to .mp3 label for user download clarity.
    const audioFile = audioBlob
      ? new File([audioBlob], 'anant-voice.opus', { type: audioBlob.type || 'audio/ogg' })
      : null;

    // Try 1: share image+audio together
    if (audioFile) {
      const combined = { title: 'Anant Social Post', text: caption || '', files: [imgFile, audioFile] };
      if (navigator.canShare && navigator.canShare(combined)) {
        try { await navigator.share(combined); toast.success('✅ Shared with audio'); return; }
        catch (e) { if (e && e.name === 'AbortError') return; }
      }
    }
    // Try 2: share image only + separately download audio for manual attach
    const imgOnly = { title: 'Anant Social Post', text: caption || '', files: [imgFile] };
    if (navigator.canShare && navigator.canShare(imgOnly)) {
      try {
        await navigator.share(imgOnly);
        if (audioFile) {
          downloadAudio();
          toast.success('🎙 Audio download ho gaya — WA chat me 📎 icon se attach karo');
        } else {
          toast.success('✅ Shared');
        }
        return;
      } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    // Try 3: hard fallback — download both, open WA with caption
    downloadImage();
    if (audioFile) {
      downloadAudio();
      toast.success('⬇️ Image + Audio downloaded · WA khul raha hai — manually attach karo');
    } else {
      toast.success('⬇️ Image downloaded · WA khul raha hai');
    }
    const url = `https://wa.me/?text=${encodeURIComponent(caption || 'Check this out')}`;
    window.open(url, '_blank');
  };

  const downloadAudio = () => {
    if (!audioBlob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(audioBlob);
    a.download = `anant-voice-${Date.now()}.opus`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const savePost = async () => {
    if (!active) { toast.error('Active plan required'); return; }
    setSaveBusy(true);
    try {
      const c = canvasRef.current;
      const thumb = c ? c.toDataURL('image/jpeg', 0.5) : null;
      await api.post('/social-share/posts/save', {
        label: (caption || 'Untitled').slice(0, 60),
        template_id: null,
        caption,
        thumbnail_data_url: thumb && thumb.length < 550000 ? thumb : null,
        meta: { tone, lang },
      });
      toast.success('✅ Saved to My Posts');
      onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaveBusy(false); }
  };

  if (!active) {
    return (
      <SubscribeGate
        title="Create Post locked"
        body={`Post banane, download karne aur share karne ke liye active plan chahiye. ${cfg?.settings?.monthly_price_at || 500} AT / month ya ${cfg?.settings?.yearly_price_at || 5000} AT / year.`}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="space-y-2">
        <div className="text-xs font-black opacity-70">Live Preview (1080×1080)</div>
        <div className="rounded-2xl overflow-hidden border-2 border-fuchsia-400/40 bg-black">
          <canvas ref={canvasRef} data-testid="social-canvas" className="w-full h-auto block" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={downloadImage} data-testid="social-download"
                  className="py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center justify-center gap-1">
            <Download size={12} /> JPG
          </button>
          <button onClick={nativeShare} data-testid="social-share-native"
                  className="py-2 rounded-lg bg-fuchsia-500 text-white text-xs font-black flex items-center justify-center gap-1">
            <Share2 size={12} /> Share
          </button>
          <button onClick={savePost} disabled={saveBusy || !active} data-testid="social-save"
                  className="py-2 rounded-lg bg-orange-500 text-slate-900 text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50">
            <Save size={12} /> Save
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2">
          <div className="text-xs font-black text-fuchsia-300 flex items-center gap-1"><ImagePlus size={12} /> Image Source</div>
          <div className="grid grid-cols-3 gap-2">
            <label className="cursor-pointer py-2 rounded-lg bg-slate-800 border border-slate-700 text-[11px] font-black text-center hover:bg-slate-700">
              📷 Camera / File
              <input type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="social-file-input" />
            </label>
            <label className="cursor-pointer py-2 rounded-lg bg-slate-800 border border-slate-700 text-[11px] font-black text-center hover:bg-slate-700">
              🖼️ Gallery
              <input type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            <div className="flex gap-1">
              <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="URL" data-testid="social-url-input"
                     className="flex-1 px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-[10px]" />
              <button onClick={addUrl} className="px-2 rounded-lg bg-fuchsia-500 text-white text-[10px] font-black">➕</button>
            </div>
          </div>
        </div>

        <div className="p-3 rounded-2xl bg-purple-950/50 border border-purple-500/40 space-y-2">
          <div className="text-xs font-black text-purple-300 flex items-center gap-1"><Sparkles size={12} /> AI Caption (20 AT)</div>
          <AIWalletGuard atBalance={cfg.at_balance || 0} threshold={200} tool="Social Share" testId="social-wallet-guard-banner" />
          <input value={intent} onChange={(e) => setIntent(e.target.value)} placeholder="Rough idea (e.g. 'Diwali saree sale 30% off')"
                 data-testid="social-ai-intent" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <div className="grid grid-cols-2 gap-2">
            <select value={tone} onChange={(e) => setTone(e.target.value)} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="social-tone">
              <option value="friendly">Friendly</option>
              <option value="professional">Professional</option>
              <option value="festive">Festive</option>
              <option value="urgent">Urgent</option>
              <option value="luxury">Luxury</option>
            </select>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="social-lang">
              <option value="hi">Hindi</option>
              <option value="en">English</option>
              <option value="mr">Marathi</option>
              <option value="hinglish">Hinglish</option>
            </select>
          </div>
          <button onClick={aiCaption} disabled={aiBusy || !active} data-testid="social-ai-generate"
                  className="w-full py-2 rounded-lg bg-purple-500 text-white text-xs font-black disabled:opacity-50">
            {aiBusy ? '✨ Generating...' : '✨ Generate Caption (20 AT)'}
          </button>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4}
                    data-testid="social-caption" placeholder="Yahaan caption likhein ya AI se generate karein…"
                    className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs resize-y" />
        </div>

        <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2">
          <div className="text-xs font-black text-amber-300 flex items-center gap-1"><Mic size={12} /> 0-Cost Voice Announcement <span className="text-[9px] opacity-70 font-normal">· auto-plays as WA voice note</span></div>
          <div className="grid grid-cols-2 gap-2">
            <select value={ttsVoice} onChange={(e) => setTtsVoice(e.target.value)} data-testid="social-tts-voice"
                    className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value="">🎤 Default voice</option>
              {(window.speechSynthesis?.getVoices?.() || []).filter((v) => /hi|en|mr/i.test(v.lang)).slice(0, 20).map((v) => (
                <option key={v.name} value={v.name}>{v.name} · {v.lang}</option>
              ))}
            </select>
            <button onClick={speakCaption} data-testid="social-tts"
                    className="py-2 rounded-lg bg-amber-500 text-slate-900 text-xs font-black">
              🔊 Preview TTS
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            <button onClick={() => genJingle('bell')}    data-testid="social-jingle-bell"    className="py-1.5 rounded bg-purple-500/40 border border-purple-400 text-white text-[10px] font-black">🔔 Bell</button>
            <button onClick={() => genJingle('cash')}    data-testid="social-jingle-cash"    className="py-1.5 rounded bg-emerald-500/40 border border-emerald-400 text-white text-[10px] font-black">💰 Cash</button>
            <button onClick={() => genJingle('festive')} data-testid="social-jingle-festive" className="py-1.5 rounded bg-amber-500/40 border border-amber-400 text-white text-[10px] font-black">🪔 Festive</button>
            <button onClick={() => genJingle('whoosh')}  data-testid="social-jingle-whoosh"  className="py-1.5 rounded bg-cyan-500/40 border border-cyan-400 text-white text-[10px] font-black">💨 Whoosh</button>
            <button onClick={() => genJingle('dhol')}    data-testid="social-jingle-dhol"    className="py-1.5 rounded bg-orange-500/40 border border-orange-400 text-white text-[10px] font-black">🥁 Dhol</button>
            <button onClick={toggleRecord} data-testid="social-record"
                    className={`py-1.5 rounded text-[10px] font-black ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-700 text-white border border-slate-500'}`}>
              {recording ? '⏹ Stop' : '🎙 Record'}
            </button>
          </div>
          {audioBlob && (
            <div className="space-y-1">
              <div className="text-[10px] text-emerald-300 font-black">✅ {audioLabel} · attached to share</div>
              <audio controls src={URL.createObjectURL(audioBlob)} className="w-full" data-testid="social-audio-preview" />
              <div className="grid grid-cols-2 gap-1">
                <button onClick={downloadAudio} data-testid="social-audio-download" className="py-1 rounded bg-slate-700 text-white text-[10px] font-black">⬇️ Download .opus</button>
                <button onClick={() => { setAudioBlob(null); setAudioLabel(''); }} className="py-1 rounded bg-red-500/30 text-red-300 text-[10px] font-black">✕ Remove Audio</button>
              </div>
            </div>
          )}
        </div>

        {!active && (
          <div className="p-2 rounded-lg bg-amber-500/20 border border-amber-400/40 text-[10px] text-amber-100 text-center">
            🔒 Save / AI ke liye Plan tab me subscribe karein
          </div>
        )}
      </div>
    </div>
  );
}

// ============ CATALOG TAB ============
function CatalogTab({ active }) {
  const [label, setLabel] = useState('');
  const [items, setItems] = useState([{ name: '', price: '', image_url: '' }]);
  const [rows, setRows] = useState([]);
  const load = () => api.get('/social-share/catalog/list').then((r) => setRows(r.data.catalogs || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!active) {
    return <SubscribeGate title="Product Catalog locked" body="Product catalog banane ke liye active subscription chahiye. Plan tab me jaake unlock karo." />;
  }
  const upd = (i, p) => setItems(items.map((x, idx) => idx === i ? { ...x, ...p } : x));
  const add = () => items.length < 12 && setItems([...items, { name: '', price: '', image_url: '' }]);
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));
  const save = async () => {
    if (!active) { toast.error('Active plan required'); return; }
    const clean = items.filter((x) => x.name.trim());
    if (clean.length === 0) { toast.error('Kam se kam 1 product'); return; }
    try {
      await api.post('/social-share/catalog/save', { label: label || 'Catalog', products: clean });
      toast.success('✅ Catalog saved'); setItems([{ name: '', price: '', image_url: '' }]); setLabel(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="space-y-3">
      <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2" data-testid="social-catalog-editor">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Catalog label (e.g. Diwali Special)" data-testid="social-catalog-label"
               className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`social-catalog-item-${i}`}>
            <input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Product name"
                   className="col-span-4 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={it.price} onChange={(e) => upd(i, { price: e.target.value })} placeholder="Price"
                   className="col-span-3 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={it.image_url} onChange={(e) => upd(i, { image_url: e.target.value })} placeholder="Image URL"
                   className="col-span-4 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <button onClick={() => del(i)} className="col-span-1 text-rose-300 text-xs"><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={add} className="px-3 py-1.5 rounded bg-slate-700 text-xs font-black">+ Add</button>
          <button onClick={save} data-testid="social-catalog-save" className="flex-1 px-3 py-1.5 rounded bg-emerald-500 text-slate-900 text-xs font-black">💾 Save Catalog</button>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="p-2 rounded-lg bg-slate-800/60 text-xs" data-testid={`social-catalog-row-${c.id}`}>
            <div className="font-black">{c.label} · {c.products?.length || 0} products</div>
            <div className="opacity-70">{c.products?.map((p) => p.name).join(', ')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ TEMPLATES TAB ============
function TemplatesTab({ active, onUse }) {
  const [rows, setRows] = useState([]);
  const [lang, setLang] = useState('');
  useEffect(() => {
    api.get('/social-share/templates', { params: { lang: lang || undefined } }).then((r) => setRows(r.data.templates || [])).catch(() => {});
  }, [lang]);
  if (!active) {
    return <SubscribeGate title="Templates locked" body="Ready-made poster templates unlock karne ke liye Plan tab me jaake subscribe karo. Har template ek-tap me post ban jata hai." />;
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="social-tpl-lang">
          <option value="">All Languages</option>
          <option value="hi">Hindi</option>
          <option value="en">English</option>
          <option value="mr">Marathi</option>
        </select>
        <div className="text-[10px] opacity-70">Tap ▶ Use · Create tab me load ho jayega</div>
      </div>
      {rows.length === 0 && <div className="text-xs opacity-60 text-center py-8">Admin abhi templates add nahi kiye. Please wait / contact admin.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {rows.map((t) => (
          <button key={t.id} onClick={() => onUse && onUse(t)}
                  data-testid={`social-tpl-${t.id}`}
                  className="text-left rounded-xl overflow-hidden bg-slate-800/60 border-2 border-slate-700 hover:border-fuchsia-400 transition group">
            <div className="relative w-full aspect-square overflow-hidden">
              {t.image_url ? (
                <img src={t.image_url} className="w-full h-full object-cover" alt="" />
              ) : (
                <div className="w-full h-full" style={{ background: t.gradient || 'linear-gradient(135deg,#7c3aed,#ec4899)' }} />
              )}
              <div className="absolute inset-0 bg-black/30 flex flex-col justify-end p-2">
                <div className="text-[9px] font-black text-amber-200 uppercase">{t.category}</div>
                <div className="text-sm font-black text-white leading-tight line-clamp-2" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>{t.label}</div>
                {t.sample_caption && (
                  <div className="text-[10px] text-white/90 mt-1 line-clamp-2" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{t.sample_caption.slice(0, 90)}</div>
                )}
              </div>
              <div className="absolute top-1 right-1 text-[9px] px-1.5 py-0.5 rounded bg-black/60 text-white">{(t.languages || []).join('/')}</div>
            </div>
            <div className="p-2 flex items-center justify-between gap-1 bg-slate-900">
              <div className="text-[10px] font-black text-fuchsia-300 truncate">{t.label}</div>
              <div className="text-[10px] px-2 py-0.5 rounded bg-fuchsia-500 text-white font-black group-hover:bg-fuchsia-400">▶ Use</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ MY POSTS TAB ============
function MyPostsTab({ active }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get('/social-share/posts/list').then((r) => setRows(r.data.posts || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!active) {
    return <SubscribeGate title="Saved posts are locked" body="Bina active subscription ke aap posts save, reshare ya download nahi kar sakte. Plan tab me jaake plan lo, aapki saari saved posts wapas milengi." />;
  }

  const requireActive = () => {
    if (!active) { toast.error('Active plan required'); return false; }
    return true;
  };

  const del = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/social-share/posts/${id}`); load(); toast.success('Deleted');
  };

  const download = (p) => {
    if (!requireActive()) return;
    if (!p.thumbnail_data_url) { toast.error('Thumbnail missing — kripya wapas save karke reshare karein'); return; }
    const a = document.createElement('a');
    a.href = p.thumbnail_data_url;
    a.download = `${(p.label || 'post').replace(/[^a-z0-9]/gi, '_')}.jpg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success('⬇️ Download shuru');
  };

  const _dataUrlToBlob = async (dataUrl) => {
    const r = await fetch(dataUrl); return r.blob();
  };

  const reshare = async (p) => {
    if (!requireActive()) return;
    setBusy(p.id);
    try {
      let files = [];
      if (p.thumbnail_data_url) {
        const blob = await _dataUrlToBlob(p.thumbnail_data_url);
        files = [new File([blob], `${(p.label || 'post').replace(/[^a-z0-9]/gi, '_')}.jpg`, { type: 'image/jpeg' })];
      }
      const payload = { title: p.label || 'Anant Social Post', text: p.caption || '', files };
      if (files.length && navigator.canShare && navigator.canShare(payload)) {
        await navigator.share(payload);
      } else if (navigator.share) {
        await navigator.share({ title: payload.title, text: payload.text });
      } else {
        if (p.thumbnail_data_url) download(p);
        const url = `https://wa.me/?text=${encodeURIComponent(p.caption || 'Check this out')}`;
        window.open(url, '_blank');
      }
      try { await api.post(`/social-share/posts/${p.id}/track-share`); } catch { /* silent */ }
      load();
    } catch (e) {
      if (e && e.name !== 'AbortError') toast.error('Share failed');
    } finally { setBusy(null); }
  };

  const shareWA = (p) => {
    if (!requireActive()) return;
    if (p.thumbnail_data_url) download(p);
    const url = `https://wa.me/?text=${encodeURIComponent(p.caption || 'Check this out')}`;
    window.open(url, '_blank');
    try { api.post(`/social-share/posts/${p.id}/track-share`); } catch { /* silent */ }
  };

  return (
    <div className="space-y-2">
      {rows.length === 0 && <div className="text-xs opacity-60 text-center py-8">Koi post save nahi. Create Post tab me jaayein.</div>}
      {rows.map((p) => (
        <div key={p.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2" data-testid={`social-post-${p.id}`}>
          <div className="flex items-start gap-3">
            {p.thumbnail_data_url ? (
              <img src={p.thumbnail_data_url} className="w-20 h-20 object-cover rounded flex-shrink-0" alt="" />
            ) : (
              <div className="w-20 h-20 rounded bg-slate-700 flex items-center justify-center text-xs opacity-60 flex-shrink-0">no img</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black truncate">{p.label}</div>
              <div className="text-[10px] opacity-70 line-clamp-2">{p.caption}</div>
              <div className="text-[9px] text-emerald-300 mt-1">📤 Shared {p.share_count || 0}× · saved {(p.created_at || '').slice(0, 10)}</div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <button onClick={() => reshare(p)} disabled={busy === p.id} data-testid={`social-post-reshare-${p.id}`}
                    className="py-1.5 rounded-lg bg-fuchsia-500 text-white text-[10px] font-black disabled:opacity-50">
              {busy === p.id ? '⏳' : '📤 Reshare'}
            </button>
            <button onClick={() => shareWA(p)} data-testid={`social-post-wa-${p.id}`}
                    className="py-1.5 rounded-lg bg-green-600 text-white text-[10px] font-black">
              💬 WA
            </button>
            <button onClick={() => download(p)} data-testid={`social-post-download-${p.id}`}
                    className="py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-[10px] font-black">
              ⬇️ Download
            </button>
            <button onClick={() => del(p.id)} data-testid={`social-post-delete-${p.id}`}
                    className="py-1.5 rounded-lg bg-red-500/30 text-red-300 text-[10px] font-black">
              ✕ Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ BRANDING TAB ============
function BrandingTab({ cfg, onReload }) {
  const active = !!cfg.active;
  const [form, setForm] = useState({
    brand_shop_name: cfg.user.brand_shop_name || '',
    brand_phone: cfg.user.brand_phone || '',
    brand_logo_url: cfg.user.brand_logo_url || '',
  });
  if (!active) {
    return <SubscribeGate title="Branding locked" body="Auto-watermark branding sirf active plan ke saath save hota hai. Plan tab me jaake unlock karo." />;
  }
  const save = async () => {
    try { await api.post('/social-share/branding', form); toast.success('✅ Branding saved'); onReload && onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2 max-w-md" data-testid="social-brand-editor">
      <div className="text-xs font-black text-fuchsia-300">🏷️ Automatic Watermark Branding</div>
      <input value={form.brand_shop_name} onChange={(e) => setForm({ ...form, brand_shop_name: e.target.value })} placeholder="Shop / Brand Name" data-testid="social-brand-name"
             className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
      <input value={form.brand_phone} onChange={(e) => setForm({ ...form, brand_phone: e.target.value })} placeholder="Phone / WhatsApp" data-testid="social-brand-phone"
             className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
      <input value={form.brand_logo_url} onChange={(e) => setForm({ ...form, brand_logo_url: e.target.value })} placeholder="Logo URL (optional)"
             className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
      <button onClick={save} data-testid="social-brand-save" className="w-full py-2 rounded-lg bg-emerald-500 text-slate-900 text-sm font-black">💾 Save Branding</button>
    </div>
  );
}

// ============ PLAN TAB ============
function PlanTab({ cfg, onReload }) {
  const [busy, setBusy] = useState(null);
  const s = cfg.settings || {};
  const active = !!cfg.active;
  const expiry = cfg.user?.plan_expiry;

  const subscribe = async (plan, renew = false) => {
    const price = plan === 'monthly' ? s.monthly_price_at : s.yearly_price_at;
    const msg = renew
      ? `Plan abhi active hai. Early renew karke aur ${plan === 'monthly' ? s.monthly_days : s.yearly_days} days add karna hai? ${price} AT deduct hoga.`
      : `${price} AT deduct hoga. Continue?`;
    if (!window.confirm(msg)) return;
    setBusy(plan);
    try {
      await api.post('/social-share/subscribe', { plan, renew });
      toast.success(renew ? `✅ Renewed · +${price} AT charged` : `✅ ${plan} plan activated`);
      onReload && onReload();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed';
      // 409 → plan already active
      if (e?.response?.status === 409) {
        if (window.confirm(`${detail}\n\nEarly renew karna hai? (${price} AT + days add ho jayenge)`)) {
          return subscribe(plan, true);
        }
        return;
      }
      toast.error(detail);
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3">
      {active && (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-2 border-emerald-400/50 p-4 text-center" data-testid="social-plan-active-banner">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🎉 Plan Active · <span className="text-white">{cfg.user.plan}</span></div>
          <CountdownTimer expiry={expiry} className="text-4xl sm:text-5xl font-black text-emerald-200 tabular-nums mt-1" onExpire={onReload} />
          <div className="text-[10px] opacity-70 mt-1">Expires · {expiry ? new Date(expiry).toLocaleString() : '—'}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`p-4 rounded-2xl border-2 ${active ? 'bg-slate-800/40 border-slate-600' : 'bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 border-fuchsia-400/40'}`} data-testid="social-plan-monthly">
          <div className="text-lg font-black">Monthly</div>
          <div className={`text-3xl font-black ${active ? 'text-slate-400' : 'text-fuchsia-300'}`}>{s.monthly_price_at} AT</div>
          <div className="text-xs opacity-70">{s.monthly_days} days · Unlimited posts · AI captions (20 AT each)</div>
          <button onClick={() => subscribe('monthly')} disabled={busy === 'monthly'}
                  data-testid="social-plan-monthly-btn"
                  className={`w-full mt-3 py-2 rounded-lg text-sm font-black disabled:opacity-50 ${active ? 'bg-slate-700 text-slate-300' : 'bg-fuchsia-500 text-white'}`}>
            {busy === 'monthly' ? '⏳' : (active ? '🔒 Plan Active · Early Renew' : `Buy Monthly · ${s.monthly_price_at} AT`)}
          </button>
        </div>
        <div className={`p-4 rounded-2xl border-2 ${active ? 'bg-slate-800/40 border-slate-600' : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-400/40'}`} data-testid="social-plan-yearly">
          <div className="text-lg font-black">Yearly · <span className="text-amber-300">2 mo free</span></div>
          <div className={`text-3xl font-black ${active ? 'text-slate-400' : 'text-amber-300'}`}>{s.yearly_price_at} AT</div>
          <div className="text-xs opacity-70">{s.yearly_days} days · Best value · Same features</div>
          <button onClick={() => subscribe('yearly')} disabled={busy === 'yearly'}
                  data-testid="social-plan-yearly-btn"
                  className={`w-full mt-3 py-2 rounded-lg text-sm font-black disabled:opacity-50 ${active ? 'bg-slate-700 text-slate-300' : 'bg-amber-500 text-slate-900'}`}>
            {busy === 'yearly' ? '⏳' : (active ? '🔒 Plan Active · Early Renew' : `Buy Yearly · ${s.yearly_price_at} AT`)}
          </button>
        </div>
      </div>

      {active && (
        <div className="text-[10px] text-center opacity-70 leading-relaxed">
          ⏳ Repeat buying blocked while plan is active. Time khatam hone par plan buttons wapas active ho jayenge.<br />
          Ya "Early Renew" click karke abhi bhi days add kar sakte ho (confirm popup aayega).
        </div>
      )}
    </div>
  );
}

// ============ PLAN STATUS BADGE (compact live countdown for header) ============
function PlanStatusBadge({ cfg }) {
  const active = !!cfg?.active;
  const expiry = cfg?.user?.plan_expiry;
  if (!active) {
    return (
      <div className="text-[10px] px-3 py-1 rounded-full border-2 border-amber-400/60 bg-amber-500/20 text-amber-200 font-black" data-testid="social-plan-badge-inactive">
        🔒 No Plan · Subscribe to unlock
      </div>
    );
  }
  return (
    <div className="text-[10px] px-3 py-1 rounded-full border-2 border-emerald-400/60 bg-emerald-500/20 text-emerald-200 font-black inline-flex items-center gap-1" data-testid="social-plan-badge-active">
      <span>✅ Active</span>
      <CountdownTimer expiry={expiry} className="tabular-nums" compact />
    </div>
  );
}

// ============ SHARED · SUBSCRIBE GATE (used across every locked tab) ============
function SubscribeGate({ title = 'Subscription required', body = '' }) {
  const goPlan = () => {
    const el = document.querySelector('[data-testid="social-tab-plan"]');
    el && el.click();
  };
  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/15 to-fuchsia-500/15 p-6 sm:p-10 text-center space-y-3" data-testid="social-tab-locked">
      <div className="text-5xl">🔒</div>
      <div className="text-lg sm:text-xl font-black text-amber-200">{title}</div>
      {body && <div className="text-xs sm:text-sm opacity-80 max-w-md mx-auto">{body}</div>}
      <button onClick={goPlan} data-testid="social-tab-locked-cta"
              className="inline-block px-5 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-amber-500 text-slate-900 text-sm font-black">
        🚀 Plan tab me jaao
      </button>
    </div>
  );
}

// ============ LIVE COUNTDOWN · DD:HH:MM:SS ============
function CountdownTimer({ expiry, className = '', compact = false, onExpire }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  let diffSec = 0;
  if (expiry) {
    try { diffSec = Math.max(0, Math.floor((new Date(expiry).getTime() - now) / 1000)); }
    catch { diffSec = 0; }
  }
  const expired = diffSec === 0;
  useEffect(() => {
    if (expired && onExpire) onExpire();
  }, [expired, onExpire]);
  if (!expiry) return null;
  const d = Math.floor(diffSec / 86400);
  const h = Math.floor((diffSec % 86400) / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  const sec = diffSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (compact) {
    return <span className={className}>· {pad(d)}d:{pad(h)}h:{pad(m)}m:{pad(sec)}s</span>;
  }
  return (
    <div className={className} data-testid="plan-countdown">
      {pad(d)}<span className="opacity-60 text-2xl">d</span>:{pad(h)}<span className="opacity-60 text-2xl">h</span>:{pad(m)}<span className="opacity-60 text-2xl">m</span>:{pad(sec)}<span className="opacity-60 text-2xl">s</span>
    </div>
  );
}

// ============ Helper: canvas wrap-text ============
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(/\s+/);
  let line = ''; let cy = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy); line = w; cy += lineHeight;
    } else { line = test; }
    if (cy > y + lineHeight * 3.5) break;
  }
  if (line) ctx.fillText(line, x, cy);
}

// ============ Helper: parse "linear-gradient(...)" stops ============
function _parseGradientStops(css) {
  if (!css) return null;
  try {
    const m = String(css).match(/(?:linear|radial)-gradient\(([^)]+)\)/i);
    if (!m) return null;
    return m[1].split(',').map((x) => x.trim()).filter((p) => p.startsWith('#') || p.startsWith('rgb'));
  } catch { return null; }
}

// ============ Helper: AudioBuffer → WAV Blob (16-bit PCM) ============
function _audioBufferToWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const sr = buffer.sampleRate;
  const samples = buffer.length;
  const bytes = samples * numCh * 2;
  const ab = new ArrayBuffer(44 + bytes);
  const view = new DataView(ab);
  const write = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  write(0, 'RIFF'); view.setUint32(4, 36 + bytes, true); write(8, 'WAVE');
  write(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, numCh, true); view.setUint32(24, sr, true);
  view.setUint32(28, sr * numCh * 2, true); view.setUint16(32, numCh * 2, true); view.setUint16(34, 16, true);
  write(36, 'data'); view.setUint32(40, bytes, true);
  let off = 44;
  for (let i = 0; i < samples; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      let s = buffer.getChannelData(ch)[i];
      s = Math.max(-1, Math.min(1, s));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      off += 2;
    }
  }
  return new Blob([view], { type: 'audio/wav' });
}
